import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Modal,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
} from '@mui/material';

// Gradient color for consistency
const gradientColor = 'linear-gradient(111.1deg, rgba(69,150,164,1) 2.5%, rgba(17,20,34,1) 100.3%)';

const AssignmentDetails = () => {
  const [activeTab, setActiveTab] = useState('upload');
  const [subjectName, setSubjectName] = useState('');

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f7fa', minHeight: '100vh' }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontWeight: 'bold',
          color: '#333',
          textAlign: 'center',
          mb: 3,
        }}
      >
        {subjectName || 'Assignment Details'}
      </Typography>

      <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 2 }}>
        <CardContent>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            centered
            sx={{
              mb: 2,
              '& .MuiTab-root': {
                fontWeight: 'bold',
              },
              '& .Mui-selected': {
                color: '#4596a4',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#4596a4',
              },
            }}
          >
            <Tab label="Assignment Upload" value="upload" />
            <Tab label="Teacher Checked Assignment" value="checked" />
          </Tabs>

          {activeTab === 'upload' && <AssignmentUpload setSubjectName={setSubjectName} />}
          {activeTab === 'checked' && <TeacherCheckedAssignment />}
        </CardContent>
      </Card>
    </Box>
  );
};

const AssignmentUpload = ({ setSubjectName }) => {
  const [assignments, setAssignments] = useState([]);
  const [uncheckedAssignments, setUncheckedAssignments] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState('');
  const [file, setFile] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAssignmentName, setSelectedAssignmentName] = useState('');

  const { subjectId } = useParams();
  const [assignmentDetails, setAssignmentDetails] = useState(null);
const [fetchingDetails, setFetchingDetails] = useState(false);

const fetchAssignmentDetails = async (assignmentId) => {
  if (!assignmentId) {
    setAssignmentDetails(null);
    return;
  }
  
  setFetchingDetails(true);
  try {
    const token = sessionStorage.getItem('token');
    const response = await fetch('https://wsmsv1.aidigitalsec.com/api/AssignmentCreate/GetAssignmentsById', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: JSON.stringify({
        assignmentId: parseInt(assignmentId, 10),
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch assignment details');
    }

    const result = await response.json();
    if (result.success && result.data && result.data.length > 0) {
      setAssignmentDetails(result.data[0]);
    }
  } catch (error) {
    console.error('Error fetching assignment details:', error);
    setError('Failed to load assignment details');
  } finally {
    setFetchingDetails(false);
  }
};

  const fetchData = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const apiUrl = 'https://wsmsv1.aidigitalsec.com/api';

      const assignmentsResponse = await fetch(`${apiUrl}/Student/Assignmentddl`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          studentId: parseInt(sessionStorage.getItem('employeeId'), 10),
          classId: parseInt(sessionStorage.getItem('classId'), 10),
          sectionId: parseInt(sessionStorage.getItem('sectionId'), 10),
          subjectId: parseInt(subjectId, 10),
          assignmentId: 0,
        }),
      });

      if (!assignmentsResponse.ok) {
        throw new Error('Failed to fetch assignments');
      }

      const assignmentsData = await assignmentsResponse.json();
      setAssignments(Array.isArray(assignmentsData) ? assignmentsData : []);

      const uncheckedResponse = await fetch(`${apiUrl}/Student/AssignmentUncheckedDetails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          studentId: parseInt(sessionStorage.getItem('employeeId'), 10),
          classId: parseInt(sessionStorage.getItem('classId'), 10),
          sectionId: parseInt(sessionStorage.getItem('sectionId'), 10),
          subjectId: parseInt(subjectId, 10),
          assignmentId: 0,
        }),
      });

      if (!uncheckedResponse.ok) {
        throw new Error('Failed to fetch unchecked assignments');
      }

      const uncheckedData = await uncheckedResponse.json();
      setUncheckedAssignments(Array.isArray(uncheckedData) ? uncheckedData : []);

      if (assignmentsData.length > 0) {
        setSubjectName(assignmentsData[0].subjectName);
      }
    } catch (error) {
      setError(error.message);
      console.error('API request error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [subjectId, setSubjectName]);

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleUpload = async () => {
    if (!selectedAssignment || !file) {
      setError('Please select an assignment and a file to upload.');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('AssignmentId', selectedAssignment);
    formData.append('StudentId', sessionStorage.getItem('employeeId'));
    formData.append('AssignmentFile', file);

    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch('https://wsmsv1.aidigitalsec.com/api/Student/UplodAssignment', {
        method: 'POST',
        headers: {
          Authorization: token,
        },
        body: formData,
      });

      if (response.ok) {
        const selected = assignments.find((a) => a.assignmentId == selectedAssignment);
        setSelectedAssignmentName(selected?.assignment || '');
        setUploadSuccess(true);
      } else {
        throw new Error('Failed to upload assignment');
      }
    } catch (error) {
      setError(error.message);
      console.error('Upload error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleView = (path) => {
    if (!path) return;
    const url = `https://wsmsv1.aidigitalsec.com/${path}`;
    window.open(url, '_blank');
  };

  const handleCloseModal = () => {
    setUploadSuccess(false);
    fetchData();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
    <Card
  sx={{
    mb: 4,
    borderRadius: 1,
    border: '1px solid rgba(255,255,255,0.2)',
    background: 'rgba(255,255,255,0.95)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 12px 30px rgba(0,0,0,0.12)',
    transition: 'all 0.3s ease',
  }}
>
  {/* Header */}
  <Box
    sx={{
      px: 3,
      py: 2.5,
      borderRadius: '12px 12px 0 0',
      background:
        'linear-gradient(111.1deg, rgba(69,150,164,1) 2.5%, rgba(17,20,34,1) 100.3%)',
      color: '#fff',
    }}
  >
    <Typography variant="h6" sx={{ fontWeight: 600 }}>
      Upload Assignment
    </Typography>
    <Typography variant="body2" sx={{ opacity: 0.85 }}>
      Select an assignment and upload your work
    </Typography>
  </Box>

  <CardContent sx={{ p: 4 }}>
    {/* Assignment Select */}
    <TextField
      select
      label="Select Assignment"
      value={selectedAssignment}
     onChange={(e) => {
    setSelectedAssignment(e.target.value);
    fetchAssignmentDetails(e.target.value);
  }}
      fullWidth
      sx={{
        mb: 3,
        '& .MuiOutlinedInput-root': {
          borderRadius: 2,
          backgroundColor: '#fafafa',
          '& fieldset': {
            borderColor: 'rgba(0,0,0,0.15)',
          },
          '&.Mui-focused fieldset': {
            borderColor: 'rgba(69,150,164,1)',
          },
        },
      }}
    >
      <MenuItem value="" disabled>
        <em>Select an assignment</em>
      </MenuItem>
      {assignments.map((assignment) => (
        <MenuItem key={assignment.assignmentId} value={assignment.assignmentId}>
          {assignment.assignment}
        </MenuItem>
      ))}
    </TextField>

    {/* Assignment Details */}
{fetchingDetails && (
  <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
    <CircularProgress size={24} />
  </Box>
)}

{assignmentDetails && !fetchingDetails && (
  <Box
    sx={{
      p: 3,
      mb: 3,
      borderRadius: 2,
      backgroundColor: '#f0f8ff',
      border: '1px solid rgba(69,150,164,0.3)',
    }}
  >
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#2c3e50' }}>
      Assignment Details
    </Typography>
    
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="body2" sx={{ color: '#34495e' }}>
        <strong>Description:</strong> {assignmentDetails.assignmentDescription || 'N/A'}
      </Typography>
      
      <Typography variant="body2" sx={{ color: '#34495e' }}>
        <strong>Assigned Date:</strong> {assignmentDetails.assignmentDate || 'N/A'}
      </Typography>
      
      <Typography variant="body2" sx={{ color: '#e74c3c', fontWeight: 500 }}>
        <strong>Last Date for Submission:</strong> {assignmentDetails.submissionDate || 'N/A'}
      </Typography>
      
      {assignmentDetails.assignmentFile ? (
        <Button
          variant="outlined"
          size="small"
          onClick={() => handleView(assignmentDetails.assignmentFile)}
          sx={{
            mt: 1,
            alignSelf: 'flex-start',
            color: 'rgba(69,150,164,1)',
            borderColor: 'rgba(69,150,164,0.6)',
            '&:hover': {
              backgroundColor: 'rgba(69,150,164,0.08)',
              borderColor: 'rgba(69,150,164,1)',
            },
          }}
        >
          View Assignment File
        </Button>
      ) :
      ( <Button
      disabled
          variant="outlined"
          size="small"
          onClick={() => handleView(assignmentDetails.assignmentFile)}
          sx={{
            mt: 1,
            alignSelf: 'flex-start',
            color: 'rgba(69,150,164,1)',
            borderColor: 'rgba(69,150,164,0.6)',
            '&:hover': {
              backgroundColor: 'rgba(69,150,164,0.08)',
              borderColor: 'rgba(69,150,164,1)',
            },
          }}
        >
          No File Available
        </Button>)}
    </Box>
  </Box>
)}

    {/* File Upload */}
    <Box
      sx={{
        p: 3,
        mb: 3,
        borderRadius: 2,
        border: '1px dashed rgba(0,0,0,0.25)',
        textAlign: 'center',
        backgroundColor: '#f9fbfc',
      }}
    >
      <input
        type="file"
        id="assignment-upload"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      <label htmlFor="assignment-upload">
       <Button
  component="span"
  sx={{
    px: 3,
    py: 1.2,
    fontWeight: 500,
    color: 'rgba(69,150,164,1)',
    backgroundColor: '#ffffff',
    border: '1.5px solid rgba(69,150,164,0.6)',
    borderRadius: 2,
    '&:hover': {
      backgroundColor: 'rgba(69,150,164,0.08)',
      borderColor: 'rgba(69,150,164,1)',
    },
  }}
>
  Choose Your Assignment
</Button>

      </label>

      {file && (
        <Typography
          variant="body2"
          sx={{
            mt: 2,
            color: '#2c3e50',
            fontWeight: 500,
            wordBreak: 'break-all',
          }}
        >
          📄 {file.name}
        </Typography>
      )}
    </Box>

    {/* Upload Button */}
 <Button
  fullWidth
  variant="contained"
  onClick={handleUpload}
  disabled={!selectedAssignment || !file || loading}
  sx={{
    py: 1.6,
    fontSize: '0.95rem',
    fontWeight: 600,
    letterSpacing: '0.3px',
    background:
      'linear-gradient(111.1deg, rgba(69,150,164,1) 2.5%, rgba(17,20,34,1) 100.3%)',
    '&:hover': {
      opacity: 0.9,
      boxShadow: '0 6px 16px rgba(69,150,164,0.4)',
    },
    '&.Mui-disabled': {
      background: 'rgba(0,0,0,0.12)',
      color: 'rgba(0,0,0,0.3)',
    },
  }}
>
  {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Upload Assignment'}
</Button>

  </CardContent>
</Card>



      <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
        <Table>
          <TableHead sx={{ background: gradientColor }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Teacher Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Assignment</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Subject Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Assignment View</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Submission Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {uncheckedAssignments.map((assignment) => (
              <TableRow key={assignment.assignmentId}>
                <TableCell>{assignment.teacherName}</TableCell>
                <TableCell>{assignment.assignment}</TableCell>
                <TableCell>{assignment.subjectName}</TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleView(assignment.assignmentUploadedFile)}
                    sx={{
                      background: '#4CAF50',
                      '&:hover': {
                        background: '#45a049',
                      },
                    }}
                  >
                    View
                  </Button>
                </TableCell>
                <TableCell>{assignment.assignmentSubmissionDate}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Success Modal */}
      <Modal open={uploadSuccess} onClose={handleCloseModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ color: 'green', fontWeight: 'bold' }}>
            Successfully Uploaded ✅
          </Typography>
          <Typography sx={{ mt: 2 }}>
            Your assignment <strong>"{selectedAssignmentName}"</strong> has been uploaded successfully.
          </Typography>
          <Button
            variant="contained"
            onClick={handleCloseModal}
            sx={{
              mt: 3,
              background: gradientColor,
              '&:hover': {
                background: gradientColor,
                opacity: 0.9,
              },
            }}
          >
            OK
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};


const TeacherCheckedAssignment = () => {
  const [checkedAssignments, setCheckedAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { subjectId } = useParams();

  useEffect(() => {
    const fetchCheckedAssignments = async () => {
      try {
        const token = sessionStorage.getItem('token');
        const response = await fetch('https://wsmsv1.aidigitalsec.com/api/Student/AssignmentCheckedDetails', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({
            studentId: parseInt(sessionStorage.getItem('employeeId'), 10),
            classId: parseInt(sessionStorage.getItem('classId'), 10),
            sectionId: parseInt(sessionStorage.getItem('sectionId'), 10),
            subjectId: parseInt(subjectId, 10),
            assignmentId: 0,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to fetch checked assignments');
        }

        const data = await response.json();
        setCheckedAssignments(Array.isArray(data) ? data : []);
      } catch (error) {
        setError(error.message);
        console.error('API request error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCheckedAssignments();
  }, [subjectId]);

  const handleViewAssignment = (path) => {
    if (!path) return;
    const url = `https://wsmsv1.aidigitalsec.com/${path}`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
  <TableContainer
  component={Paper}
  sx={{
    boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
    borderRadius: 2,
    overflow: 'hidden',
  }}
>
  <Table>
    {/* Header */}
    <TableHead
      sx={{
        background:
          'linear-gradient(111.1deg, rgba(69,150,164,1) 2.5%, rgba(17,20,34,1) 100.3%)',
      }}
    >
      <TableRow>
        {[
          'Teacher Name',
          'Assignment',
          'Subject',
          'Assigned Date',
          'Submission Date',
          'Assignment View',
          'Status',
          'Teacher Remarks',
        ].map((head) => (
          <TableCell
            key={head}
            sx={{
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.85rem',
              whiteSpace: 'nowrap',
            }}
          >
            {head}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>

    {/* Body */}
    <TableBody>
      {checkedAssignments.length === 0 ? (
        <TableRow>
          <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
            No assignments available
          </TableCell>
        </TableRow>
      ) : (
        checkedAssignments.map((assignment) => (
          <TableRow
            key={assignment.assignmentId}
            hover
            sx={{ '&:last-child td': { borderBottom: 0 } }}
          >
            <TableCell>{assignment.teacherName || '-'}</TableCell>
            <TableCell>{assignment.assignment || '-'}</TableCell>
            <TableCell>{assignment.subjectName || '-'}</TableCell>
            <TableCell>{assignment.assignmentDate || '-'}</TableCell>
            <TableCell>{assignment.assignmentSubmissionDate || '-'}</TableCell>

            {/* View */}
            <TableCell>
              {assignment.studentSubmittedFile ? (
                <Button
                  size="small"
                  onClick={() =>
                    handleViewAssignment(assignment.studentSubmittedFile)
                  }
                  sx={{
                    px: 2,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#fff',
                    background:
                      'linear-gradient(111.1deg, rgba(69,150,164,1) 2.5%, rgba(17,20,34,1) 100.3%)',
                    '&:hover': { opacity: 0.9 },
                  }}
                >
                  View
                </Button>
              ) : (
                '-'
              )}
            </TableCell>

             {/* Status */}
            <TableCell>
              <Box
                sx={{
                  display: 'inline-block',
                  px: 1.5,
                  py: 0.4,
                  borderRadius: 10,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: assignment.isChecked ? '#2e7d32' : '#ed6c02',
                  backgroundColor: assignment.isChecked
                    ? 'rgba(46,125,50,0.1)'
                    : 'rgba(237,108,2,0.1)',
                }}
              >
                {assignment.isChecked ? 'Checked' : 'Not Checked'}
              </Box>
            </TableCell>

            {/* Teacher Remarks */}
            <TableCell
              sx={{
                maxWidth: 200,
                color: assignment.teacherRemarks ? '#2c3e50' : '#9e9e9e',
                fontSize: '0.8rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={assignment.teacherRemarks || 'No remarks'}
            >
              {assignment.teacherRemarks || '—'}
            </TableCell>

           
          </TableRow>
        ))
      )}
    </TableBody>
  </Table>
</TableContainer>


  );
};

export default AssignmentDetails;
